import { useEffect, useState } from "react";
import { Shield, Users, FileText, CalendarRange, BarChart3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminDashboard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [programName, setProgramName] = useState("");

  const loadUsers = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    loadUsers();
    api.get("/admin/settings").then(({ data }) => setProgramName(data?.program_name || "")).catch(() => {});
  }, []);

  const setRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}/role`, { role }); toast.success(lang === "es" ? "Rol actualizado." : "Role updated."); loadUsers(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeUser = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success(lang === "es" ? "Usuario eliminado." : "User removed."); loadUsers(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveSettings = async () => {
    try { await api.put("/admin/settings", { program_name: programName }); toast.success(lang === "es" ? "Guardado." : "Saved."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const statCards = stats ? [
    { icon: Users, label: lang === "es" ? "Usuarios activos" : "Active users", value: stats.total_users },
    { icon: FileText, label: lang === "es" ? "Actividades generadas" : "Activities generated", value: stats.activities_generated },
    { icon: CalendarRange, label: lang === "es" ? "Planes semanales" : "Weekly plans", value: stats.weekly_plans_generated },
    { icon: Users, label: lang === "es" ? "Maestros" : "Teachers", value: stats.teachers },
  ] : [];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-2xl bg-accent/15 flex items-center justify-center"><Shield className="h-6 w-6 text-accent" /></span>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{lang === "es" ? "Panel del Administrador" : "Administrator Dashboard"}</h1>
        </div>

        <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="rounded-2xl border border-border bg-card p-5" data-testid={`stat-${i}`}>
                <Icon className="h-5 w-5 text-primary" />
                <p className="font-heading font-extrabold text-3xl mt-3">{s.value}</p>
                <p className="text-sm text-muted-foreground">{s.label}</p>
              </div>
            );
          })}
        </section>

        {stats && (
          <section className="grid sm:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><BarChart3 className="h-5 w-5 text-secondary" /> {lang === "es" ? "Temas más usados" : "Most-used themes"}</h3>
              {stats.top_themes?.length ? stats.top_themes.map((th, i) => (
                <div key={i} className="flex justify-between py-1 text-sm"><span>{th.name}</span><span className="text-muted-foreground">{th.count}</span></div>
              )) : <p className="text-muted-foreground text-sm">—</p>}
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><BarChart3 className="h-5 w-5 text-secondary" /> {lang === "es" ? "Áreas más usadas" : "Most-used domains"}</h3>
              {stats.top_domains?.length ? stats.top_domains.map((d, i) => (
                <div key={i} className="flex justify-between py-1 text-sm"><span>{Array.isArray(d.name) ? d.name.join(", ") : d.name}</span><span className="text-muted-foreground">{d.count}</span></div>
              )) : <p className="text-muted-foreground text-sm">—</p>}
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-border bg-card p-5 space-y-3">
          <h3 className="font-heading font-bold">{lang === "es" ? "Configuración del programa / Marca" : "Program Settings / Branding"}</h3>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-2 flex-1 min-w-[240px]">
              <Label>{lang === "es" ? "Nombre del programa" : "Program name"}</Label>
              <Input value={programName} onChange={(e) => setProgramName(e.target.value)} className="h-11 rounded-xl" data-testid="admin-program-name" />
            </div>
            <Button onClick={saveSettings} className="rounded-full bg-primary" data-testid="admin-save-settings">{lang === "es" ? "Guardar" : "Save"}</Button>
          </div>
          <p className="text-xs text-muted-foreground">{lang === "es" ? "El logotipo oficial de Brilla se podrá subir aquí en el futuro." : "The official Brilla logo can be uploaded here in a future release."}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading font-bold text-xl">{lang === "es" ? "Gestión de usuarios" : "User Management"}</h2>
          <div className="rounded-2xl border border-border overflow-hidden">
            {users.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-border last:border-0" data-testid={`user-row-${u.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{u.name} <span className="text-muted-foreground font-normal">· {u.email}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                    <SelectTrigger className="h-9 w-40 rounded-lg" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teacher">Teacher</SelectItem>
                      <SelectItem value="coach">Coach</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                  {u.id !== user?.id && (
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeUser(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
